import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { catalogError, toClientError } from "@/lib/errors";
import { hashOtp, safeCompareHashes } from "@/lib/email/otp-email";

const emailSchema = z.string().email().max(255);

/**
 * Mark an address verified on whichever profile row owns it.
 *
 * The lookup is by email rather than by user id because the OTP flow runs
 * before a session exists, so the server function has no authenticated user to
 * read an id from. `handle_new_user()` creates the profile on `auth.users`
 * insert, so the row normally exists by the time the user submits a code.
 *
 * @param {string} email Lower-cased address that just verified.
 * @param {Date} now Verification timestamp.
 * @returns {Promise<{ id: string } | null>} The profile id if found.
 */
async function markProfileVerified(email, now) {
  const { data: profile, error: selectErr } = await supabaseAdmin
    .from("profiles")
    .select("id")
    .eq("email", email)
    .maybeSingle();

  if (selectErr) {
    throw await toClientError(selectErr, { area: "auth.otp.verify" });
  }

  if (!profile) {
    // No profile yet: the auth trigger has not run or the address was renamed
    // between sign-up and verification. Not fatal, but it means nothing was
    // recorded, so it is worth a server-side breadcrumb.
    console.error(`[OTP] Verified ${email} but no profile row matched`);
    return null;
  }

  const { error: updateErr } = await supabaseAdmin
    .from("profiles")
    .update({ email_verified: true, email_verified_at: now.toISOString() })
    .eq("id", profile.id);

  if (updateErr) {
    throw await toClientError(updateErr, { area: "auth.otp.verify" });
  }

  return { id: profile.id };
}

/**
 * Exchange a 6-digit code for a confirmed, signed-in session.
 *
 * After a successful check the function now: burns the stored row, marks the
 * profile verified, confirms the auth user (so the OTP fully replaces the
 * Supabase confirmation email rather than sitting in front of it), and returns
 * the tokens the browser needs to sign in without asking the user to type their
 * password a second time. The previous version returned nothing but
 * `{ success: true }`, which left the freshly signed-up user with no session:
 * the client navigated to `/dashboard`, the guard bounced them straight back to
 * `/auth`, and the verification appeared to do nothing.
 */
export const verifyOtp = createServerFn({ method: "POST" })
  .validator((d) => {
    const schema = z.object({
      email: emailSchema,
      code: z.string().regex(/^\d{6}$/),
    });
    return schema.parse(d);
  })
  .handler(async ({ data }) => {
    const email = data.email.trim().toLowerCase();

    const { data: otp, error: fetchErr } = await supabaseAdmin
      .from("email_otps")
      .select("*")
      .eq("email", email)
      .maybeSingle();

    if (fetchErr) {
      throw await toClientError(fetchErr, { area: "auth.otp.verify" });
    }

    if (!otp) {
      throw catalogError("AUTH_INVALID_CODE", {
        area: "auth.otp.verify",
        message: "Invalid or expired code.",
      });
    }

    const now = new Date();

    if (new Date(otp.expires_at) < now) {
      await supabaseAdmin.from("email_otps").delete().eq("email", email);
      throw catalogError("AUTH_CODE_EXPIRED", {
        area: "auth.otp.verify",
        message: "That code has expired. Request a new one.",
      });
    }

    if (!safeCompareHashes(hashOtp(data.code), otp.hash)) {
      // Wrong codes are counted but never fatal: the earlier lockout after
      // five mistakes burned a code the user had legitimately received and
      // told them to request a new one, which is exactly the moment they are
      // already struggling. The counter stays so a brute-force attempt is
      // still visible in the row and in the server log.
      const attempts = (otp.attempts || 0) + 1;
      await supabaseAdmin.from("email_otps").update({ attempts }).eq("email", email);

      if (attempts % 5 === 0) {
        console.warn(`[OTP] ${attempts} wrong codes for ${email}; code not burned`);
      }

      throw catalogError("AUTH_INVALID_CODE", {
        area: "auth.otp.verify",
        message: "That code isn't right.",
      });
    }

    // Code accepted. Burn it so a single code cannot be replayed.
    await supabaseAdmin.from("email_otps").delete().eq("email", email);

    const profile = await markProfileVerified(email, now);

    // Confirm the auth user and mint a session. The profile id is the auth
    // user id (profiles references auth.users), so we can update the user
    // directly without enumerating all users.
    let userId = profile?.id;

    if (!userId) {
      // Fallback: look up the user by email only if the profile wasn't found.
      const { data: userList, error: listErr } = await supabaseAdmin.auth.admin.listUsers({
        page: 1,
        perPage: 1000,
      });

      if (listErr) {
        throw await toClientError(listErr, { area: "auth.otp.verify" });
      }

      const user = userList?.users?.find((u) => u.email?.toLowerCase() === email);

      if (!user) {
        throw catalogError("NOT_FOUND", {
          area: "auth.otp.verify",
          message: "We couldn't find the account for that email. Try signing up again.",
        });
      }

      userId = user.id;
    }

    // `email_confirm: true` is what replaces the Supabase confirmation email:
    // without it, a project with "Confirm email" enabled still refuses to issue
    // a session after this verification.
    const { error: confirmErr } = await supabaseAdmin.auth.admin.updateUserById(userId, {
      email_confirm: true,
    });

    if (confirmErr) {
      throw await toClientError(confirmErr, { area: "auth.otp.verify" });
    }

    return { success: true, emailVerified: true, userId };
  });
